import { TFunction } from 'i18next';
import get from 'lodash/get';
import { I18nEntry, LanguageOption } from '@monorepo/types';

/**
 * Gets a dynamic translation using i18n entries from MongoDB documents.
 *
 * This function looks up translation keys from a document's i18n array based on the provided
 * mongoField, and returns the translated value using the found key or falls back
 * to the original field value. The function will fall back to the original value if:
 * - No i18n entries exist for the field
 * - No translation key is found for the field
 * - The translation key exists but returns an empty string or whitespace-only string
 *
 * @param mongoField - The MongoDB field name to look up in the i18n entries.
 *                     For nested objects, use dot notation (e.g., "settings.description")
 * @param document - The document containing i18n entries and the original field data
 * @param t - The translation function from react-i18next
 * @param language - The current i18n language code. When English, the raw DB field is returned
 *                   directly because English is the source language and the admin.json files
 *                   are synced from Lokalize with a delay, making them stale after admin edits.
 * @returns The translated string using the found i18n key or the original field value as fallback
 *
 * @example
 * ```tsx
 * const { t } = useTranslation();
 * const document = {
 *   name: "Original Name",
 *   settings: { description: "Original Description" },
 *   i18n: [
 *     { mongoField: "name", keyName: "contest.name.123" },
 *     { mongoField: "settings.description", keyName: "contest.description.123" }
 *   ]
 * };
 *
 * // If translation returns empty string, will fall back to "Original Name"
 * const translatedName = getDynamicTranslation("name", document, t);
 * const translatedDesc = getDynamicTranslation("settings.description", document, t);
 * ```
 */
export const getDynamicTranslation = (
  mongoField: string,
  document: { i18n?: I18nEntry[]; [key: string]: any },
  t: TFunction<any>,
  language?: string,
): string => {
  // English is the Lokalize source language. The admin.json files on S3 are synced
  // with a delay after admin edits, so the cached translation is stale. The raw DB
  // field is always up-to-date and is used directly for English.
  if (language === LanguageOption.ENGLISH) {
    return get(document, mongoField);
  }

  if (!document?.['i18n']) {
    return get(document, mongoField);
  }

  const keyName = Array.isArray(document['i18n'])
    ? document['i18n'].find((i18n: I18nEntry) => i18n.mongoField === mongoField)
        ?.localiseKeyName
    : undefined;

  if (!keyName) {
    return get(document, mongoField);
  }

  const keyNameWithFilename = `admin:${keyName}`;
  const originalValue = get(document, mongoField);

  // Get the translation result
  const translatedValue = t(keyNameWithFilename, {
    defaultValue: originalValue,
  });

  // Check if translation result is empty, null, undefined, or whitespace-only
  // If so, fall back to the original value
  if (!translatedValue || translatedValue.trim() === '') {
    return originalValue;
  }

  return translatedValue;
};

/**
 * Gets a dynamic translation for array items using i18n entries from MongoDB documents.
 *
 * This function looks up translation keys from i18n entries for array items based on the provided
 * idFieldValue and fieldPath, and returns the translated value using the found key or falls back
 * to the original field value. The function will fall back to the original value if:
 * - No i18n entries exist
 * - No translation key is found matching the idFieldValue and fieldPath
 * - The translation key exists but returns an empty string or whitespace-only string
 *
 * Key name format: admin_${collectionName}_${arrayFieldPath}_${idFieldKeyName}_${idFieldValue}_${fieldPath}_${documentId}
 * - at(-1) = documentId
 * - at(-2) = fieldPath
 * - at(-3) = idFieldValue
 *
 * @param originalValue - The original value to use as fallback
 * @param i18nEntries - Array of i18n entries for the array field path
 * @param idFieldValue - The ID value of the array item to translate (e.g., 'uuid, id, etc.')
 * @param fieldPath - The field path within the item that will be translated (e.g., 'label')
 * @param t - The translation function from react-i18next
 * @param language - The current i18n language code. When English, the raw original value is
 *                   returned directly to avoid stale Lokalize-synced admin.json content.
 * @returns The translated string using the found i18n key or the original value as fallback
 *
 * @example
 * ```tsx
 * const { t } = useTranslation();
 * const i18nEntries = i18nArrays['footerConfig.links'];
 * const translatedLabel = getDynamicTranslationForArray(
 *   label,
 *   i18nEntries,
 *   id,
 *   'label',
 *   t
 * );
 * ```
 */
export const getDynamicTranslationForArray = (
  originalValue: string,
  i18nEntries: I18nEntry[],
  idFieldValue: string,
  fieldPath: string,
  t: TFunction<any>,
  language?: string,
): string => {
  if (language === LanguageOption.ENGLISH) {
    return originalValue;
  }

  if (!i18nEntries || i18nEntries.length === 0) {
    return originalValue;
  }

  const keyName = i18nEntries.find((i18n: I18nEntry) => {
    if (!i18n.localiseKeyName) {
      return false;
    }

    // Extract fieldPath and idFieldValue from keyName
    // Key format: admin_${collectionName}_${arrayFieldPath}_${idFieldKeyName}_${idFieldValue}_${fieldPath}_${documentId}
    const parts = i18n.localiseKeyName.split('_');
    const extractedFieldPath = parts.at(-2); // Second from end
    const extractedId = parts.at(-3); // Third from end

    // Match both the idFieldValue and fieldPath
    return extractedId === idFieldValue && extractedFieldPath === fieldPath;
  })?.localiseKeyName;

  if (!keyName) {
    return originalValue;
  }

  const keyNameWithFilename = `admin:${keyName}`;
  const translatedValue = t(keyNameWithFilename, {
    defaultValue: originalValue,
  });

  // Check if translation result is empty, null, undefined, or whitespace-only
  // If so, fall back to the original value
  if (!translatedValue || translatedValue.trim() === '') {
    return originalValue;
  }

  return translatedValue;
};
